import { SectionHeader } from "./Shared";
import { MapPin, CheckCircle2 } from "lucide-react";

const PRIMARY = ["Ocean County, NJ", "Monmouth County, NJ"];
const SECONDARY = [
  "Middlesex County",
  "Burlington County",
  "Mercer County",
  "Union County",
  "Somerset County",
  "Atlantic County",
];

export const ServiceArea = () => {
  return (
    <section
      id="service-area"
      data-testid="service-area-section"
      className="relative py-24 sm:py-32 bg-[#080808] border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
        {/* Left: Copy */}
        <div className="lg:col-span-6">
          <SectionHeader
            eyebrow="Coverage · service map"
            title={
              <>
                Proudly serving{" "}
                <span className="text-[#00E5FF] italic font-light">
                  New Jersey.
                </span>
              </>
            }
            sub="Headquartered at the Jersey Shore — we service all of New Jersey, with primary coverage across Ocean and Monmouth counties."
          />

          <div className="mt-10">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
              Primary coverage
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {PRIMARY.map((area) => (
                <span
                  key={area}
                  data-testid={`primary-area-${area.split(",")[0].replace(/\s+/g, "-").toLowerCase()}`}
                  className="inline-flex items-center gap-2 rounded-full bg-[#00E5FF] text-black px-4 py-2 text-sm font-semibold font-body"
                >
                  <MapPin size={14} />
                  {area}
                </span>
              ))}
            </div>

            <div className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
              Also available across NJ
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {SECONDARY.map((area) => (
                <span
                  key={area}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm text-white/75 font-body"
                >
                  {area}
                </span>
              ))}
            </div>

            <p className="mt-8 text-sm text-white/50 font-body leading-relaxed max-w-lg">
              Outside these areas? We still may be able to help —{" "}
              <a
                href="mailto:info@theneatlabllc.com?subject=Service%20Area%20Inquiry"
                data-testid="service-area-email-link"
                className="text-[#00E5FF] hover:underline"
              >
                send us a quick email
              </a>{" "}
              and we'll let you know.
            </p>
          </div>
        </div>

        {/* Right: Stylized NJ map panel */}
        <div className="lg:col-span-6">
          <div className="relative rounded-3xl border border-white/10 bg-black/40 p-8 sm:p-10 overflow-hidden aspect-square max-w-md mx-auto">
            <div className="absolute inset-0 opacity-50 pointer-events-none">
              <div className="absolute top-1/3 right-1/4 h-40 w-40 rounded-full bg-[#00E5FF]/20 blur-[80px]" />
              <div className="absolute bottom-1/4 left-1/3 h-32 w-32 rounded-full bg-[#00E5FF]/15 blur-[80px]" />
            </div>

            {/* Grid */}
            <div
              className="absolute inset-0 opacity-[0.12]"
              style={{
                backgroundImage:
                  "linear-gradient(to right, rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.4) 1px, transparent 1px)",
                backgroundSize: "32px 32px",
              }}
            />

            {/* NJ silhouette (accurate simplified outline) */}
            <svg
              viewBox="0 0 320 420"
              className="relative z-10 w-full h-full"
              fill="none"
            >
              <defs>
                <linearGradient id="njFill" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.12" />
                  <stop offset="100%" stopColor="#00E5FF" stopOpacity="0.03" />
                </linearGradient>
              </defs>

              {/* New Jersey shape */}
              <path
                d="M 118 18
                   L 138 12
                   L 175 22
                   L 210 38
                   L 232 58
                   L 248 78
                   L 258 96
                   L 250 108
                   L 232 112
                   L 218 122
                   L 214 140
                   L 224 158
                   L 238 176
                   L 248 198
                   L 254 222
                   L 256 248
                   L 250 272
                   L 240 294
                   L 226 318
                   L 210 340
                   L 192 360
                   L 172 378
                   L 154 390
                   L 138 398
                   L 124 402
                   L 116 396
                   L 112 380
                   L 114 358
                   L 110 336
                   L 104 316
                   L 98 294
                   L 92 270
                   L 86 244
                   L 80 218
                   L 74 192
                   L 70 166
                   L 72 140
                   L 78 114
                   L 84 90
                   L 92 68
                   L 100 48
                   L 108 32 Z"
                stroke="rgba(0,229,255,0.55)"
                strokeWidth="1.5"
                fill="url(#njFill)"
                strokeLinejoin="round"
              />

              {/* Monmouth County pin (northeast coastal) */}
              <g>
                <circle cx="230" cy="155" r="16" stroke="#00E5FF" strokeOpacity="0.3" strokeWidth="1" />
                <circle cx="230" cy="155" r="6" fill="#00E5FF">
                  <animate
                    attributeName="r"
                    values="5;9;5"
                    dur="2.2s"
                    repeatCount="indefinite"
                  />
                </circle>
              </g>

              {/* Ocean County pin (just below Monmouth, east coast) */}
              <g>
                <circle cx="232" cy="215" r="18" stroke="#00E5FF" strokeOpacity="0.4" strokeWidth="1" />
                <circle cx="232" cy="215" r="7" fill="#00E5FF">
                  <animate
                    attributeName="r"
                    values="6;11;6"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </circle>
              </g>

              {/* Pin labels */}
              <g fontFamily="monospace" fontSize="10" letterSpacing="1.2" fill="#00E5FF">
                <line x1="230" y1="155" x2="270" y2="135" stroke="#00E5FF" strokeOpacity="0.4" strokeWidth="1" />
                <text x="272" y="132">MONMOUTH</text>
                <line x1="232" y1="215" x2="272" y2="235" stroke="#00E5FF" strokeOpacity="0.4" strokeWidth="1" />
                <text x="274" y="238">OCEAN</text>
              </g>

              {/* Compass */}
              <g transform="translate(36, 380)" fontFamily="monospace" fontSize="9" fill="rgba(255,255,255,0.35)">
                <text x="0" y="0">N ↑</text>
              </g>
            </svg>

            <div className="absolute top-5 left-5 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-[#00E5FF] animate-pulse" />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
                Live · NJ
              </span>
            </div>

            <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">
              <span>Coverage map</span>
              <span className="flex items-center gap-1 text-[#00E5FF]">
                <CheckCircle2 size={12} /> Active
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
